import { ArrowUp, Check, Link2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { BackstageStrip } from './BackstageStrip'
import { BACKSTAGE, COMPLETE, COMPOSER_LABEL, PRIORITIES, STEPS, useBackstageRun } from './cfoSetup'
import { ChatOptions } from './ChatOptions'
import { CFO_SETUP_THREAD, Speaker } from './contacts'
import { BlackMark } from './FleetTopNav'
import { LearningCharacter } from './LearningCharacter'
import { MessageList } from './MessageList'
import { SetupProgress } from './SetupProgress'
import { SignedInAvatar } from './SignedInAvatar'

const RAIL_WIDTH = 200

/** The width the agent's chat has on the Cai tab; the two are the same chat. */
const CHAT_WIDTH = 520

/**
 * Enabling CFO, on the insurance application's layout: the stepper down the
 * left, the conversation doing the work. Opened in its own tab, so the
 * account's product tabs are not here to switch away to.
 */
export function CfoSetupDesktop({ className }: { className?: string }) {
  const [priority, setPriority] = useState<string | null>(null)
  // Bumped on every send; the change of key is what replays the animation.
  const [sent, setSent] = useState(0)
  const { running, exiting } = useBackstageRun(sent)
  // Picking one sends it: the options stand in for typing, not for a form field.
  const messages = priority ? [...CFO_SETUP_THREAD, { from: Speaker.YOU, text: priority }] : CFO_SETUP_THREAD

  return (
    <div className={cn('border-ink-100 flex flex-col overflow-hidden border bg-white', className)}>
      <div className="border-ink-100 flex shrink-0 items-center gap-2 border-b px-4 py-2.5">
        <BlackMark />
        <span className="text-ink text-xs font-semibold">Add CFO capabilities to Cai</span>
        <span className="ml-auto flex items-center gap-2">
          <ShareCaiButton />
          <SignedInAvatar />
        </span>
      </div>

      {/* The shared Progress paints its indicator bg-primary and forwards only
          the root's class, so the black is set through the indicator's slot. */}
      <Progress
        value={COMPLETE}
        aria-label="Setup progress"
        className="bg-ink-50 [&_[data-slot=progress-indicator]]:bg-ink-700 h-1 shrink-0 rounded-none"
      />

      <BackstageStrip items={BACKSTAGE} current={running} exiting={exiting} />

      <div className="relative flex min-h-0 flex-1">
        <LearningCharacter sent={sent} working={running >= 0} />

        {/* Out of the centred column, so the chat sits in the middle of the
            screen rather than beside the rail. */}
        <div className="absolute top-3 left-6" style={{ width: RAIL_WIDTH }}>
          <SetupProgress steps={STEPS} />
        </div>

        <div className="flex min-h-0 w-full flex-1 flex-col pb-6">
          <MessageList
            fadeTop
            animateLast={priority !== null}
            contentWidth={CHAT_WIDTH}
            messages={messages}
            trailing={
              !priority && (
                <ChatOptions
                  options={PRIORITIES}
                  onSelect={(entry) => {
                    setPriority(entry)
                    setSent((count) => count + 1)
                  }}
                />
              )
            }
          />
          <div className="mx-auto flex w-full items-center px-3 pt-2" style={{ maxWidth: CHAT_WIDTH }}>
            <div className="border-input bg-surface hover:border-ink-300 flex flex-1 items-center gap-2 rounded-2xl border px-3 py-2 transition-colors">
              <span className="text-ink-400 flex-1 truncate text-xs">{COMPOSER_LABEL}</span>
              <Button
                variant="iconOnly"
                aria-label="Send"
                onClick={() => setSent((count) => count + 1)}
                className="bg-brand hover:bg-brand-600 size-6 shrink-0 rounded-full p-0"
              >
                <ArrowUp className="text-ink-900 size-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * The quotes page's share control, pointed at the assistant instead of a set
 * of quotes. No clipboard here: there is no real link behind a prototype.
 */
function ShareCaiButton() {
  const [copied, setCopied] = useState(false)

  return (
    <Button
      type="button"
      variant="link"
      size="sm"
      className="gap-1.5"
      onClick={() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }}
    >
      {copied ? <Check className="size-4" /> : <Link2 className="size-4" />}
      Share Cai
    </Button>
  )
}
